import type { ColumnPinningPosition } from '@tanstack/react-table';
import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { Text } from '../../../../../typography/text/Text.js';
import { dataTableMessages } from '../../../messages.js';
import * as styles from './DataTableColumnsSectionHeader.sty.js';

// TanStack uses direction-agnostic "start"/"end" for pinning position;
// DataTable is always left-to-right, so our UI labels say "left"/"right".
const sectionMessages = {
  start: dataTableMessages.columnSettingsSectionPinnedLeft,
  end: dataTableMessages.columnSettingsSectionPinnedRight,
  false: dataTableMessages.columnSettingsSectionUnpinned,
} as const;

interface DataTableColumnsSectionHeaderProps {
  /**
   * The section the header labels.
   */
  position: ColumnPinningPosition;
  /**
   * The virtual index for grid row positioning.
   */
  virtualIndex: number;
  /**
   * Callback to measure the element for virtualization.
   */
  measureElement: (element: Element | null) => void;
}

/**
 * Section header row in the column settings list, labeling the pinned/unpinned
 * groups of columns.
 * @internal
 */
export function DataTableColumnsSectionHeader(
  props: Readonly<DataTableColumnsSectionHeaderProps>,
): JSX.Element {
  const { position, virtualIndex, measureElement } = props;
  const intl = useIntl();
  const label = sectionMessages[`${position}`];

  return (
    <h3
      ref={measureElement}
      data-index={virtualIndex}
      className={styles.sectionHeader}
      style={{ gridRowStart: virtualIndex + 1 }}
    >
      <Text textStyle="small-emphasized" className={styles.sectionHeaderLabel}>
        {intl.formatMessage(label)}
      </Text>
    </h3>
  );
}
